import React, { useState } from 'react'

import { InviteCodeUI } from '~/components/screens/InviteCodeUI'
import { secureCache } from '~/utils/secureCache'
import { useHistory } from 'react-router-dom'
import api from '~/api'

const ERROR_MESSAGE =
  'Invite code not recognized. Please double-check the code in your enrollment info or ask your HR team for help.'

interface InviteCodeValidationResponse {
  valid: boolean
  inviteCodeId?: number
  error?: string
  // Hand-rolled, so nothing type-checks this against the BFF: absent has to read as false, which
  // it does, and this app has no version gate, so a tab opened before the deploy sees no field.
  collectPreferredName?: boolean
}

/**
 * Unauthenticated invite code validation screen.
 */
const InviteCode: React.FC = () => {
  const history = useHistory()
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (inviteCode: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await api.rest.post<InviteCodeValidationResponse>(
        '/bff/app/signup/invite-code-validation/',
        { inviteCode, patient_creation_source: 'web' },
      )

      if (response.valid) {
        secureCache.set('inviteCode', inviteCode)
        // The switch state, not a destination: an unauthenticated client cannot read a waffle
        // switch, so the BFF reports whether prospects give a preferred name and this screen still
        // owns the navigation.
        history.push({
          pathname: response.collectPreferredName ? '/signup/prospect-name' : '/signup/name',
        })
      } else {
        setError(ERROR_MESSAGE)
      }
    } catch {
      // API returns 400 for invalid codes
      setError(ERROR_MESSAGE)
    } finally {
      setIsLoading(false)
    }
  }

  return <InviteCodeUI onSubmit={handleSubmit} error={error} isLoading={isLoading} />
}

export default InviteCode
